import { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user: userData, companies } = response.data;
    localStorage.setItem('token', token);
    if (companies) localStorage.setItem('companies', JSON.stringify(companies));
    setUser(userData);
    return { user: userData, companies: companies || [] };
  };

  const register = async (data) => {
    const response = await api.post('/auth/register', data);
    const { token, user: userData, companies } = response.data;
    localStorage.setItem('token', token);
    if (companies) localStorage.setItem('companies', JSON.stringify(companies));
    setUser(userData);
    return { user: userData, companies: companies || [] };
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('companies');
    setUser(null);
  };

  const isMasterAdmin = () => !!user?.is_master_admin;
  const isAdmin = () => user?.role === 'CI Manager' || isMasterAdmin();
  const isApprover = () => user?.role === 'Approver' || user?.role === 'CI Manager' || isMasterAdmin();
  const isSubmitter = () => user?.role === 'Submitter';

  return (
    <AuthContext.Provider value={{ 
      user,
      setUser,
      loading, 
      login, 
      register, 
      logout,
      isMasterAdmin,
      isAdmin,
      isApprover,
      isSubmitter
    }}>
      {children}
    </AuthContext.Provider>
  );
};
